import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { glacierPolicyApiCommitTransactionTestMutation } from '@/lib/heyapi/@tanstack/react-query.gen';
import { useMutation } from '@tanstack/react-query';
import { formatErrorMessage } from '../../utils';
import { ErrorDialog } from './ErrorDialog';
import type { Message } from '../HeaderView';

// Component for the confirmation dialog to lock a policy
const LockPolicyDialog = ({
  isOpen,
  onOpenChange,
  isLoading,
  setLoading,
  setMessage,
  policyId,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  setMessage: (message: Message | undefined) => void;
  policyId: string;
}) => {
  const lockTransactionAdmin = useMutation({
    ...glacierPolicyApiCommitTransactionTestMutation(),
    onSuccess() {
      setLoading(false);
      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">
              Transaction Locked! Reloading Data
            </code>
          </pre>
        ),
      });
      // Delay 2 seconds and reload the page
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    },
    onError(error: any) {
      setLoading(false);

      let title = 'Error: Transaction Lock Failed';
      const content = formatErrorMessage(error);
      console.log('error', error);

      try {
        title = `Error: Transaction Lock Failed`;
      } catch (e) {
        console.log('error', e);
      }

      setMessage({
        content: (
          <ErrorDialog title={title} onClose={() => setMessage(undefined)}>
            {content}
          </ErrorDialog>
        ),
        level: 'ERROR',
      });
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Lock Policy
          </DialogTitle>
          <DialogDescription>
            Are you sure you wish to lock the policy?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setLoading(true);
              setMessage({
                content: 'Running policy lock',
                level: 'INFO',
              });
              lockTransactionAdmin.mutate({
                path: { account_id: policyId },
              });
              onOpenChange(false);
            }}
            className="bg-red-600 text-white hover:bg-red-700"
            disabled={isLoading}
          >
            {isLoading ? 'Locking Policy...' : 'Lock Policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LockPolicyDialog;
